import React, { useCallback, useEffect } from 'react';
import costFragmentsService from '../../services/claim/cost_fragments.service';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import { CheckBoxCell, InputCell } from './CellTypes';
import StaffProviderDropdown from '../externalWorkers/StaffProviderDropdown';
import ItemTypeDropdown from '../consumables/ItemTypeDropdown';
import BusinessUnitDropdown from './BusinessUnitDropdown';
import CommercialProjectToggle from './CommercialProjectToggle';
import { VF } from '@rd-web-markets/shared/dist/models/ParamValidator';
import { ProjectSelector } from '@rd-web-markets/shared/dist/claim_project_report/ProjectSelector';
export const CostFragmentCell = _ref => {
  let {
    claim,
    refreshClaim,
    costFragments,
    setCostFragments,
    refreshCostFragments,
    costFragmentType,
    cellType,
    fieldName,
    costFragment,
    model = 'costFragment',
    triggerFragmentUpdate,
    userUnauthorizedToEdit,
    disabled,
    isTotalsRow = false,
    translateValue = false,
    allProjects,
    value,
    onToggleExpand,
    isExpanded,
    setTotals,
    onDropdownEditingChange
  } = _ref;
  const updateCostFragment = costFragmentsService.useUpdate(costFragments, setCostFragments, setTotals);
  const updateFragmentable = costFragmentsService.useUpdateFragmentable(costFragments, setCostFragments, setTotals);
  const createCostFragmentAndFragmentable = costFragmentsService.useCreate(costFragments, setCostFragments, setTotals);

  // const isNewRow = VF.isString(costFragment.id) && costFragment.id.includes('new_')
  // console.log('cost fragment: ', costFragment)
  // console.log('isNewRow: ', isNewRow)

  const onChangeSetStateData = (event, costFragmentId) => {
    const index = costFragments.findIndex(cf => cf.id === costFragmentId);
    const eventValue = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    costFragments[index][event.target.name] = eventValue;

    // With React Data Grid's editor pattern, setState is now fast (only re-renders the editor)
    setCostFragments([...costFragments]);
  };
  const onBlurSaveData = useErrorHandling(useCallback(async costFragmentId => {
    if (VF.isString(costFragmentId) && costFragmentId.includes('new_')) {
      await createCostFragmentAndFragmentable(costFragmentId);
    } else {
      await updateCostFragment(costFragmentId);
    }
  }, [createCostFragmentAndFragmentable, updateCostFragment]));
  const onChangeSetFragmentableStateData = (event, costFragmentId) => {
    var _event$sourceInfo;
    (_event$sourceInfo = event.sourceInfo) === null || _event$sourceInfo === void 0 || (_event$sourceInfo = _event$sourceInfo.event) === null || _event$sourceInfo === void 0 || _event$sourceInfo.stopPropagation();
    const costFragment = costFragments.find(cf => cf.id === costFragmentId);
    const eventValue = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (triggerFragmentUpdate && event.target.name === 'national_insurance' && eventValue === 0) {
      costFragment.fragmentable.override_national_insurance = true;
    }
    costFragment.fragmentable[event.target.name] = eventValue;

    // With React Data Grid's editor pattern, setState is now fast (only re-renders the editor)
    setCostFragments([...costFragments]);
  };
  const onBlurSaveFragmentableData = useErrorHandling(useCallback(async costFragmentId => {
    if (typeof costFragment.id === 'string' && costFragment.id.includes('new_')) {
      await createCostFragmentAndFragmentable(costFragmentId);
    } else {
      await updateFragmentable(costFragmentId);
    }
    if (triggerFragmentUpdate) {
      onBlurSaveData(costFragmentId);
    }
  }, [costFragment, createCostFragmentAndFragmentable, updateFragmentable, onBlurSaveData, triggerFragmentUpdate]));
  const eventHandlers = {
    change: model === 'fragmentable' ? onChangeSetFragmentableStateData : onChangeSetStateData,
    blur: model === 'fragmentable' ? onBlurSaveFragmentableData : onBlurSaveData
  };

  // Notify parent when ProjectSelector is being edited (to increase row height and column width)
  useEffect(() => {
    if (cellType === 'ProjectSelector' && onDropdownEditingChange) {
      onDropdownEditingChange(costFragment.id, fieldName, true);
      return () => {
        onDropdownEditingChange(costFragment.id, fieldName, false);
      };
    }
  }, [cellType, costFragment.id, fieldName, onDropdownEditingChange]);
  const editableObject = model === 'fragmentable' ? costFragment.fragmentable : costFragment;
  if (isTotalsRow) {
    let formattedValue = value;
    const numericValue = parseFloat(value);
    if (!isNaN(numericValue)) {
      formattedValue = numericValue.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "DataGridTable-totals-cell",
      style: {
        fontWeight: 'bold',
        textAlign: 'center',
        padding: '4px 8px'
      }
    }, formattedValue !== null && formattedValue !== void 0 ? formattedValue : '-');
  }
  const isCostIncludedProject = costFragment.claim_project_report.program_type === 'connected_to_program_commercial_included_cost';
  const disabledFieldsForCostIncludedProjects = ['base_salary', 'bonus', 'national_insurance', 'pension', 'reimbursed_expenses', 'total', 'capitalised_costs', 'direct_rnd_percentage', 'indirect_rnd_percentage', 'consolidated_percentage_of_time_spent', 'rnd_amount', 'pnl_expenditure', 's1308_claim'];
  const isDisabledForCostIncludedProject = isCostIncludedProject && disabledFieldsForCostIncludedProjects.includes(fieldName);
  const isDisabled = disabled || isDisabledForCostIncludedProject;
  const isEligibleRndPercentageForCostIncludedProject = isCostIncludedProject && fieldName === 'eligible_rnd_percentage';
  switch (cellType) {
    case 'text':
      return /*#__PURE__*/React.createElement(InputCell, {
        cellType: 'text',
        claim: claim,
        editableObject: editableObject,
        fieldName: fieldName,
        userUnauthorizedToEdit: userUnauthorizedToEdit,
        onChange: event => eventHandlers.change(event, costFragment.id),
        onBlur: () => eventHandlers.blur(costFragment.id),
        refreshClaim: refreshClaim,
        disabled: isDisabled,
        translateValue: translateValue
      });
    case 'number':
      return /*#__PURE__*/React.createElement(InputCell, {
        cellType: 'number',
        claim: claim,
        editableObject: editableObject,
        fieldName: fieldName,
        userUnauthorizedToEdit: userUnauthorizedToEdit,
        onChange: event => eventHandlers.change(event, costFragment.id),
        onBlur: () => eventHandlers.blur(costFragment.id),
        refreshClaim: refreshClaim,
        decimalScale: 15,
        disabled: isDisabled && !isEligibleRndPercentageForCostIncludedProject
      });
    case 'percentage':
      return /*#__PURE__*/React.createElement(InputCell, {
        cellType: 'number',
        claim: claim,
        editableObject: editableObject,
        fieldName: fieldName,
        userUnauthorizedToEdit: userUnauthorizedToEdit,
        onChange: event => eventHandlers.change(event, costFragment.id),
        onBlur: () => eventHandlers.blur(costFragment.id),
        refreshClaim: refreshClaim,
        decimalScale: 15,
        disabled: isDisabled && !isEligibleRndPercentageForCostIncludedProject,
        suffix: "%"
      });
    case 'checkbox':
      return /*#__PURE__*/React.createElement(CheckBoxCell, {
        claim: claim,
        editableObject: editableObject,
        fieldName: fieldName,
        userUnauthorizedToEdit: userUnauthorizedToEdit,
        onChange: event => eventHandlers.change(event, costFragment.id),
        onBlur: () => eventHandlers.blur(costFragment.id),
        refreshClaim: refreshClaim,
        disabled: isDisabled
      });
    case 'StaffProviderDropdown':
      return /*#__PURE__*/React.createElement(StaffProviderDropdown, {
        claim: claim,
        claimExternalWorker: editableObject,
        userUnauthorizedToEdit: userUnauthorizedToEdit,
        onChange: event => eventHandlers.change(event, costFragment.id),
        onBlur: () => eventHandlers.blur(costFragment.id),
        refreshClaim: refreshClaim,
        disabled: isDisabled
      });
    case 'ItemTypeDropdown':
      return /*#__PURE__*/React.createElement(ItemTypeDropdown, {
        claim: claim,
        claimConsumable: editableObject,
        userUnauthorizedToEdit: userUnauthorizedToEdit,
        onChange: event => eventHandlers.change(event, costFragment.id),
        onBlur: () => eventHandlers.blur(costFragment.id),
        disabled: isDisabled
      });
    case 'BusinessUnitDropdown':
      return /*#__PURE__*/React.createElement(BusinessUnitDropdown, {
        claim: claim,
        costFragment: editableObject,
        userUnauthorizedToEdit: userUnauthorizedToEdit,
        onChange: event => eventHandlers.change(event, costFragment.id),
        onBlur: () => eventHandlers.blur(costFragment.id),
        disabled: isDisabled
      });
    case 'CommercialProjectToggle':
      return /*#__PURE__*/React.createElement(CommercialProjectToggle, {
        costFragment: editableObject,
        onChange: event => eventHandlers.change(event, costFragment.id),
        onToggleExpand: onToggleExpand,
        isExpanded: isExpanded
      });
    case 'ProjectSelector':
      return /*#__PURE__*/React.createElement(ProjectSelector, {
        className: 'CostFragment-DataGrid-ClaimProjectReportDropdown',
        claimGroupId: claim.claim_group.id,
        alreadySelectedInstances: [costFragment.claim_project_report],
        onChange: event => eventHandlers.change(event, costFragment.id),
        onBlur: () => eventHandlers.blur(costFragment.id),
        disabled: isDisabled,
        eventFieldName: 'claim_project_report_id'
      });
    default:
      return 'OTHER';
  }
};